import { useState, useCallback, useRef } from "react";
import { useAuth } from "../services/useAuth";
import { getApprovalPlugin } from "../approvals/registry";

const itemAllowsFileUpload = (item) =>
  !!(item?.allowFileUpload ?? item?.currentApproval?.allowFileUpload);

/**
 * Runs approve/reject via type plugin (entity list pages, e.g. Orders).
 */
export function useApprovalActions({
  type,
  stage = "user",
  canWrite = false,
  onNotify,
  onAfterSuccess,
}) {
  const { getToken } = useAuth();
  const plugin = getApprovalPlugin(type);
  const stageConfig = plugin?.stages?.[stage];

  const [actionLoading, setActionLoading] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [attachmentTarget, setAttachmentTarget] = useState(null);

  const callbacksRef = useRef({ onNotify, onAfterSuccess });
  callbacksRef.current = { onNotify, onAfterSuccess };

  const loadingKey = (id, action) => `${id}_${action}`;

  const getId = useCallback(
    (item) =>
      plugin?.getId?.(item) ?? item?.entityId ?? item?.id ?? item?._id,
    [plugin],
  );

  const isActionLoading = useCallback(
    (item, action) => actionLoading === loadingKey(getId(item), action),
    [actionLoading, getId],
  );

  const canActOn = useCallback(
    (item) => stageConfig?.canActOn?.(item) ?? false,
    [stageConfig],
  );

  const runApprove = useCallback(
    async (item, options = {}) => {
      if (!canWrite || !item || !stageConfig) return null;
      const id = getId(item);
      if (!id) return null;

      try {
        setActionLoading(loadingKey(id, "approve"));
        const data = await stageConfig.approve(item, {
          token: getToken(),
          attachment: options.attachment,
        });
        if (data?.success !== false) {
          callbacksRef.current.onNotify?.(
            stageConfig.messages.approveSuccess,
            "success",
          );
          await stageConfig.onApproveSuccess?.(data, item, {});
          callbacksRef.current.onAfterSuccess?.(data, item);
          return data;
        }
        callbacksRef.current.onNotify?.(
          data?.message || "Onay başarısız",
          "danger",
        );
        return null;
      } catch (err) {
        callbacksRef.current.onNotify?.(
          err.response?.data?.message || "Onay başarısız",
          "danger",
        );
        return null;
      } finally {
        setActionLoading(null);
      }
    },
    [canWrite, stageConfig, getId, getToken],
  );

  const approve = useCallback(
    async (item, options = {}) => {
      if (!canWrite || !item || !stageConfig) return null;
      if (options.attachment !== undefined || !itemAllowsFileUpload(item)) {
        return runApprove(item, options);
      }
      setAttachmentTarget(item);
      return null;
    },
    [canWrite, stageConfig, runApprove],
  );

  const cancelAttachment = useCallback(() => {
    setAttachmentTarget(null);
  }, []);

  const confirmAttachment = useCallback(
    async (attachment) => {
      if (!attachmentTarget) return null;
      const data = await runApprove(attachmentTarget, { attachment });
      if (data) setAttachmentTarget(null);
      return data;
    },
    [attachmentTarget, runApprove],
  );

  const startReject = useCallback((item) => {
    setRejectTarget(item);
    setRejectReason("");
  }, []);

  const cancelReject = useCallback(() => {
    setRejectTarget(null);
    setRejectReason("");
  }, []);

  const confirmReject = useCallback(async () => {
    if (!canWrite || !rejectTarget || !stageConfig) return null;
    const id = getId(rejectTarget);
    if (!id) return null;

    try {
      setActionLoading(loadingKey(id, "reject"));
      const data = await stageConfig.reject(rejectTarget, rejectReason, {
        token: getToken(),
      });
      if (data?.success !== false) {
        callbacksRef.current.onNotify?.(
          stageConfig.messages.rejectSuccess,
          "success",
        );
        cancelReject();
        callbacksRef.current.onAfterSuccess?.(data, rejectTarget);
        return data;
      }
      callbacksRef.current.onNotify?.(
        data?.message || "Red işlemi başarısız",
        "danger",
      );
      return null;
    } catch (err) {
      callbacksRef.current.onNotify?.(
        err.response?.data?.message || "Red işlemi başarısız",
        "danger",
      );
      return null;
    } finally {
      setActionLoading(null);
    }
  }, [
    canWrite,
    rejectTarget,
    rejectReason,
    stageConfig,
    getId,
    getToken,
    cancelReject,
  ]);

  return {
    plugin,
    stageConfig,
    approve,
    startReject,
    cancelReject,
    confirmReject,
    rejectTarget,
    rejectReason,
    setRejectReason,
    attachmentTarget,
    cancelAttachment,
    confirmAttachment,
    isActionLoading,
    canActOn,
    getId,
  };
}
